import { api } from '@/shared/axios-client';
import { queryOptions, useQuery } from '@tanstack/react-query';

interface NonExecutableCategories {
  import: number[];
  blank: number[];
  comment: number[];
  decorator: number[];
  typeDeclaration: number[];
}
interface NonExecutable {
  lines: number[];
  categories: NonExecutableCategories;
}
interface Executable {
  lines: number[];
}
interface ASTNode {
  type: 'program' | 'class' | 'method' | 'function' | 'branch' | 'loop';
  name?: string;
  lineStart: number;
  lineEnd: number;
  executableLines: number[];
  nonExecutableLines: number[];
  children: ASTNode[];
}
interface AST {
  type: 'program';
  children: ASTNode[];
}
interface FileDetail {
  fileName: string;
  filePath: string;
  totalLines: number;
  totalExecutableLines: number;
  totalNonExecutableLines: number;
  totalClasses: number;
  totalMethods: number;
  totalFunctions: number;
  totalBranches: number;
  totalLoops: number;
  error: string | null;
  abstractSyntaxTree: AST;
}
interface Pagination {
  totalFiles: number;
  returnedFiles: number;
  offset: number;
  limit: number;
}
export interface PollerState {
  createdAt: string;
  interval: number;
  lastCodePolledTime: string;
  lastPollType: string;
  lastPolledTime: string;
  lastTestPolledTime: string;
  nextScheduledPoll: string;
  updatedAt: string;
}
interface ScanReport {
  scanId: string;
  commitId?: string;
  totalFiles: number;
  totalLines: number;
  totalMethods: number;
  totalFunctions: number;
  totalClasses: number;
  totalNonExecutableLines: number;
  totalExecutableLines: number;
  totalBranches: number;
  totalLoops: number;
  pagination: Pagination;
  fileDetails: FileDetail[];
  fileIndex?: Array<Omit<FileDetail, 'abstractSyntaxTree'>>;
  pollerState?: PollerState;
}

interface apiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: ScanReport;
}

export const SCAN_INVENTORY_PAGE_SIZE = 1;

export const getComponentDetails = (
  scanId: string,
  offset = 0,
  limit = SCAN_INVENTORY_PAGE_SIZE,
  filePath?: string,
): Promise<apiResponse> => {
  return api.post('/api/scanInventory', {
    scanId,
    limit,
    offset,
    ...(filePath ? { filePath } : {}),
  });
};

export const getComponentDetailQueryOptions = (scanId: string, offset = 0) => {
  return queryOptions({
    queryKey: ['component-details-view', scanId, offset],
    queryFn: () => getComponentDetails(scanId, offset),
    enabled: !!scanId,
  });
};

export const useComponentLDetail = (scanId: string, offset = 0) => {
  return useQuery({
    ...getComponentDetailQueryOptions(scanId, offset),
  });
};

export const useScanInventoryFile = (
  scanId: string,
  filePath: string,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['component-file-ast', scanId, filePath],
    queryFn: () => getComponentDetails(scanId, 0, 1, filePath),
    enabled: Boolean(scanId && filePath && enabled),
  });
};
